import { useGlobalToasts } from '@src/hooks/Toast/useGlobalToasts';
import { Dialog } from '../components/Dialog';
import { JsonModal } from '../components/JsonModal';
import { NotificationVariants } from '../components/Notification';
import { useMixpanel } from '../contexts';
import { MixpanelEvent } from '../tokens';
import { EMPTY_OBJECT, logger } from '../utils';
import { useCallbackRef } from './useCallbackRef';
import { useDisclosureWithContext } from './useDisclosureWithContext';

interface UseJsonModalParams {
  /** When handleClickJson is invoked, you can have a customMixpanelEvent be sent. The default sent Mixpanel event is `MixpanelEvent.ShowJSON`. */
  customMixpanelEvent?: MixpanelEvent;
}

/**
 * A helper hook encapsulating the logic needed to show a Json Modal with some data inside it
 * @returns `handleClickJson`, to be invoked whenever you want to show some data in the json modal, and then `jsonModal` which is the ReactNode
 * of the json modal itself
 */
export function useJsonModal<R>({ customMixpanelEvent }: UseJsonModalParams = EMPTY_OBJECT) {
  const jsonModal = useDisclosureWithContext<string>();
  const mixpanel = useMixpanel();
  const { add } = useGlobalToasts();

  const handleClickJson = useCallbackRef((data: R | undefined) => {
    mixpanel.track(customMixpanelEvent ?? MixpanelEvent.ShowJSON);
    try {
      const selectedJsonData =
        data instanceof Map ? JSON.stringify(mapToObject(data), replacer, 2) : JSON.stringify(data, replacer, 2);
      jsonModal.open(selectedJsonData);
    } catch (e) {
      logger.error(e as Error);
      add({
        text: `Unable to display JSON: ${e instanceof Error ? e.message : String(e)}`,
        variant: NotificationVariants.Negative,
      });
    }
  });

  return {
    handleClickJson,
    jsonModal: (
      <Dialog
        key="json-modal"
        isOpen={jsonModal.isOpen}
        close={jsonModal.close}
        onCloseComplete={jsonModal.onCloseComplete}
        dataTestId="show-json-modal"
        title="Show JSON"
        showClose
        suppressFooter
      >
        {jsonModal.context ? <JsonModal isVisible={jsonModal.isOpen}>{jsonModal.context}</JsonModal> : null}
      </Dialog>
    ),
  };
}

function mapToObject(aMap: Map<any, any>) {
  const obj = Object.create(null);
  for (const [k, v] of aMap) {
    if (v instanceof Map) {
      obj[k.toString()] = mapToObject(v); // handle Maps that have Maps as values
    } else {
      // calling toString handles case where map key is not a string JSON requires key to be a string
      obj[k.toString()] = v;
    }
  }
  return obj;
}

function replacer(key: string, value: any) {
  if (value instanceof Map) {
    return mapToObject(value);
  } else {
    return value;
  }
}
